<template>
  <div class="profile-manager">
    <!-- <div v-if="profileTypeRef == ProfileType.ResearchRestricted">
      <button type="button" class="btn btn-primary" @click="onCreateTrackingProfile">
        Create linked profile
      </button>
    </div> -->
    <div v-if="!isResearchRef" class="owner-area">
      <div v-if="profileTypeRef == ProfileType.User" class="alert alert-primary alert-sm">
        This is the owner's personal profile.
      </div>
      <label class="form-label">
        <span>Profile owner</span>
      </label>
      <UserItem :user-id="dataGroupRef?.ownerId"/>
      <div class="owner-actions">
        <button type="button" v-if="canTransferRef" class="btn btn-inline btn-link" data-bs-toggle="modal" data-bs-target="#transferModal">
          Change
        </button>
        <button type="button" v-if="canRequestTransferRef" class="btn btn-inline btn-link" data-bs-toggle="modal" data-bs-target="#requestTransferModal">
          Change
        </button>
      </div>
    </div>
    <div v-if="profileTypeRef == ProfileType.ManagedLinked">
      <label class="form-label">
        Linked to tree profile
      </label>
      <ProfileCard :person-id="trackedPersonIdRef"></ProfileCard>
      <button type="button" v-if="canEditRef" class="btn btn-inline btn-link action" data-bs-toggle="modal" data-bs-target="#confirmUnlinkModal">
        Unlink
      </button>
    </div>
    <div v-if="showIsChildRef" class="child-option">
      <label class="form-label">
        Profile options
      </label>
      <div class="form-check">
        <input type="checkbox" id="childOption" class="form-check-input" v-model="isChildRef" :disabled="!canEditRef">
        <label for="childOption" class="form-check-label">
          This person is a child
        </label>
        <InfoButton @click="showChildOptionInfoRef = !showChildOptionInfoRef"/>
      </div>
      <div v-if="showChildOptionInfoRef" class="help-text">
        Use this option to enable special features for children under age 13 that help protect them and their information.
      </div>
      <button type="button" v-if="showAddUserRef" class="btn btn-primary" @click="onAddChildUser">
        Create child account
      </button>
    </div>
    <div>
      <label class="form-label">
        Last modified
      </label>
      <div class="input-value">
        <RelativeDate :value="groupRef?.dataModifiedDate"/>
      </div>
    </div>
    <div class="status">
      <label class="form-label">
        Processing status
      </label>
      <div>
        <span class="badge" :class="slowGroupStatusRef">
          {{ slowGroupStatusRef }}
        </span>
        <button type="button" v-if="canReprocessRef" class="btn btn-inline btn-link btn-sm" @click="reprocessAsync">
          Reprocess
        </button>
      </div>
    </div>
    <div class="actions">
      <button type="button" v-if="canEditRef" class="btn btn-inline btn-link btn-sm" data-bs-toggle="modal" data-bs-target="#confirmCleanModal">
        Remove unused data
      </button>
      <button type="button" class="btn btn-inline btn-link btn-sm" @click="downloadAsync">
        <DownloadIcon/>
        Download (JSON)
      </button>
      <button type="button" v-if="canDeleteRef" class="btn btn-danger btn-sm" data-bs-toggle="modal" data-bs-target="#confirmDeleteModal">
        <Trash2Icon/>
        Delete profile
      </button>
    </div>
    <Modal id="confirmUnlinkModal"
      ref="confirmUnlinkModalRef"
      ok-style="warning" 
      @ok="unlinkProfile">
      <template #title>Unlink profile</template>
      <p>
        Unlinking this profile will allow it to be edited. It will no longer be updated automatically when the matching tree profile changes.
      </p>
      <p>
        <b>This operation cannot be undone.</b>
      </p>
      <p>
        Do you want to continue?
      </p>
      <template #okText>Unlink</template>
      <template #busyText>Unlinking...</template>
    </Modal>
    <UserAddModal ref="userAddModalRef"/>
    <Modal id="confirmCleanModal"
      ref="confirmCleanModalRef"
      ok-style="warning"
      @ok="cleanAsync">
      <template #title>Remove unused data</template>
      <div>
        This will delete data that was added to this profile at some point but is no longer referenced.
      </div>
      <template #okText>Remove unused</template>
    </Modal>
    <Modal id="confirmDeleteModal"
      ref="confirmDeleteModalRef"
      ok-style="danger"
      @ok="deleteProfile">
      <template #title>Delete profile</template>
      <p>
        All information in this profile will be permanently deleted.
      </p>
      <p>
        Do you want to continue?
      </p>
      <template #okIcon><Trash2Icon></Trash2Icon></template>
      <template #okText>Delete</template>
      <template #busyText>Deleting...</template>
    </Modal>
    <DataGroupTransferModal id="transferModal" :data-group-id="dataGroupRef?.id"/>
    <DataGroupRequestTransferModal id="requestTransferModal" :data-group-id="dataGroupRef?.id"/>
  </div>
</template>

<style lang="scss" scoped>
.profile-manager {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.owner-actions {
  font-size: 0.875rem;
}

.status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;

  .badge {
    margin-right: 0.5rem;
    font-size: inherit;
    font-weight: normal;
    text-transform: none;
    color: black;

    &.Incomplete {
      background-color: var(--bs-warning-bg-subtle);
    }

    &.Completed {
      background-color: #eee;
    }
  }
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: start;

  button.btn-link {
    font-size: 0.875rem;
  }

  .stars {
    display: inline-block;

    svg:nth-child(0) {
      width: 1rem;
    }
    svg:nth-child(1) {
      width: 0.5rem;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useUserStore } from '@/gp/UserStore'
import { useGroupStore } from '@/rd/GroupStore'
import { usePersonStore } from '@/rd/PersonStore'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { useTrackingLinkStore } from '@/rd/TrackingLinkStore'
import { ItemType } from '@/rd/ResearchDataModel'
import { useTokenManager } from '@/auth/TokenManager'
import { DataGroupType, ItemPermissions } from '@/gp/GroupAdminModel'
import { ProfileType, ProfileUtils } from './ProfileUtils'
import { CompositeId } from '@/rd/CompositeId'
import { PatchChange } from '@/util/Api'
import { LoadMode } from '@/util/AsyncData'
import { DownloadIcon, Trash2Icon, StarIcon } from '@zhuowenli/vue-feather-icons'
import { refThrottled } from '@vueuse/core'
import UserItem from '@/manage/users/UserItem.vue'
import ProfileCard from '@/manage/ProfileCard.vue'
import InfoButton from '@/util/InfoButton.vue'
import Modal from '@/util/Modal.vue'
import UserAddModal from '@/manage/users/UserAddModal.vue'
import RelativeDate from '@/util/RelativeDate.vue'
import DataGroupTransferModal from '../datagroups/DataGroupTransferModal.vue'
import DataGroupRequestTransferModal from '../datagroups/DataGroupRequestTransferModal.vue'

const props = defineProps<{
  personId?: string,
}>()

const isChildRef = ref(false)
const showChildOptionInfoRef = ref(false)

const confirmUnlinkModalRef = ref<InstanceType<typeof Modal>>()
const userAddModalRef = ref<InstanceType<typeof UserAddModal>>()
const confirmCleanModalRef = ref<InstanceType<typeof Modal>>()
const confirmDeleteModalRef = ref<InstanceType<typeof Modal>>()

const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const userStore = useUserStore()
const groupStore = useGroupStore()
const personStore = usePersonStore()
const workspaceStore = useWorkspaceStore()
const trackingLinkStore = useTrackingLinkStore()
const router = useRouter()
const tokenManager = useTokenManager()

// Async data
const groupIdRef = computed(() => CompositeId.getGroupId(props.personId))
const asyncDataGroupRef = computed(() => dataGroupStore.getAsyncGroup(groupIdRef.value, LoadMode.EnsureLoaded))
const asyncDataGroupMemberKeysRef = computed(() => dataGroupMemberStore.getAsyncKeyListsForDataGroups([groupIdRef.value], LoadMode.EnsureLoaded).at(0))
const asyncPermissionsRef = computed(() => dataGroupStore.getAsyncPermissions(groupIdRef.value, LoadMode.EnsureLoaded))
const asyncGroupRef = computed(() => groupStore.getAsyncGroupList([groupIdRef.value], LoadMode.EnsureLoaded).at(0))
const asyncPersonRef = computed(() => personStore.getAsyncPerson(props.personId, LoadMode.EnsureLoaded))
const asyncWorkspaceRef = computed(() => workspaceStore.getAsyncWorkspacesForGroups([groupIdRef.value], LoadMode.EnsureLoaded).at(0))
const asyncTrackedItemLinkRef = computed(() => trackingLinkStore.getAsyncTrackingLink(ItemType.Person, props.personId, LoadMode.EnsureLoaded))

// Loaded data
const personRef = computed(() => asyncPersonRef.value?.data)
const dataGroupRef = computed(() => asyncDataGroupRef.value?.data)
const ownerRef = computed(() => userStore.getLoadedUser(dataGroupRef.value?.ownerId)) // loaded with group
const groupRef = computed(() => asyncGroupRef.value?.data)
const workspaceRef = computed(() => asyncWorkspaceRef.value?.data)

// Computed data
const groupStatusRef = computed(() => groupRef.value?.status)
const slowGroupStatusRef = refThrottled(groupStatusRef, 1000, true, true)
const trackedPersonIdRef = computed(() => asyncTrackedItemLinkRef.value?.data?.trackedItemId)
const profileTypeRef = computed(() => ProfileUtils.getProfileType(personRef.value, dataGroupRef.value, ownerRef.value))
const permissionsRef = computed(() => asyncPermissionsRef.value?.data ?? ItemPermissions.None)
const isOwnerRef = computed(() => (permissionsRef.value & ItemPermissions.Owner) > 0)
const canEditRef = computed(() => (permissionsRef.value & ItemPermissions.Modify) > 0)
const canTransferRef = computed(() => isOwnerRef.value && profileTypeRef.value != ProfileType.User && !tokenManager.current.isSupervised)
const canRequestTransferRef = computed(() => !isOwnerRef.value && profileTypeRef.value != ProfileType.User && !tokenManager.current.isSupervised)
const canReprocessRef = computed(() => canEditRef.value && groupRef.value?.profile)
const canDeleteRef = computed(() => (permissionsRef.value & ItemPermissions.Delete) > 0 && profileTypeRef.value != ProfileType.User)

const showAddUserRef = computed(() => isOwnerRef.value && profileTypeRef.value == ProfileType.Managed && dataGroupRef.value?.isChild)
const isResearchRef = computed(() => dataGroupRef.value?.groupType == DataGroupType.Research)
const showIsChildRef = computed(() => profileTypeRef.value == ProfileType.Managed || profileTypeRef.value == ProfileType.User)

watch(() => props.personId, () => {
  isChildRef.value = dataGroupRef.value?.isChild ?? false // loaded with page data
  showChildOptionInfoRef.value = false
}, { immediate: true })

watch(isChildRef, async () => {
  if (dataGroupRef.value && isChildRef.value != dataGroupRef.value.isChild) {
    const change = new PatchChange('/isChild', isChildRef.value)
    await dataGroupStore.patchAsync(dataGroupRef.value!.id!, [change])
  }
})

async function onCreateTrackingProfile() {
  alert("track me!")
}

async function unlinkProfile() {
  await dataGroupStore.unlinkAsync(dataGroupRef.value!.id!)
  confirmUnlinkModalRef.value?.close()
}

async function reprocessAsync() {
  await groupStore.reprocessAsync(dataGroupRef.value!.id!, true)
}

async function downloadAsync() {
  const blob = new Blob([await groupStore.getGroupDataAsync(dataGroupRef.value!.id!)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  window.open(url)
}

async function cleanAsync() {
  try {
    await groupStore.cleanAsync(dataGroupRef.value!.id!)
    confirmCleanModalRef.value?.close()
  }
  finally {
    confirmCleanModalRef.value?.reset()
  }
}

async function deleteProfile() {
  await dataGroupStore.deleteAsync(dataGroupRef.value!.id!)
  confirmDeleteModalRef.value?.close()
  router.push('/profiles')
}

function onAddChildUser() {
  userAddModalRef.value?.open(props.personId!)
}
</script>
